import { useEffect, useRef, useState } from 'react';

/** Themed listbox; retains keyboard selection, type-ahead and label focus. */
export function Select({
  id,
  value,
  options,
  disabled,
  onChange,
}: {
  id: string;
  value: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const search = useRef({ text: '', at: 0 });
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  useEffect(() => {
    if (open)
      document.getElementById(`${id}-option-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open, id]);
  function choose(index: number) {
    const option = options[index];
    if (option) onChange(option.value);
    setOpen(false);
    button.current?.focus();
  }
  return (
    <div
      className="themed-select"
      ref={root}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button
        id={id}
        ref={button}
        type="button"
        className="select-trigger"
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-options`}
        aria-haspopup="listbox"
        aria-activedescendant={open ? `${id}-option-${active}` : undefined}
        onClick={() => {
          setActive(
            Math.max(
              0,
              options.findIndex((o) => o.value === value),
            ),
          );
          setOpen(!open);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            return;
          }
          if (e.key === 'Tab') {
            setOpen(false);
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (open) choose(active);
            else {
              setActive(
                Math.max(
                  0,
                  options.findIndex((o) => o.value === value),
                ),
              );
              setOpen(true);
            }
            return;
          }
          if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
            e.preventDefault();
            setOpen(true);
            const index = open
              ? active
              : Math.max(
                  0,
                  options.findIndex((o) => o.value === value),
                );
            setActive(
              e.key === 'Home'
                ? 0
                : e.key === 'End'
                  ? options.length - 1
                  : Math.max(
                      0,
                      Math.min(options.length - 1, index + (e.key === 'ArrowDown' ? 1 : -1)),
                    ),
            );
          } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            search.current = {
              text:
                (Date.now() - search.current.at < 700 ? search.current.text : '') +
                e.key.toLowerCase(),
              at: Date.now(),
            };
            const index = options.findIndex((o) =>
              o.label.toLowerCase().startsWith(search.current.text),
            );
            if (index >= 0) {
              setOpen(true);
              setActive(index);
            }
          }
        }}
      >
        <span>{options.find((o) => o.value === value)?.label ?? 'Choose…'}</span>
        <span aria-hidden="true">⌄</span>
      </button>
      {open && (
        <div id={`${id}-options`} role="listbox" aria-labelledby={id} className="select-options">
          {options.map((o, i) => (
            <div
              key={o.value}
              id={`${id}-option-${i}`}
              role="option"
              aria-selected={o.value === value}
              className={i === active ? 'highlighted' : ''}
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => setActive(i)}
              onClick={() => choose(i)}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
